import { Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AccountsReceivableService } from './accounts-receivable.service';
import { AccountsPayableService } from './accounts-payable.service';
import { AccountsReceivable } from '../models/AccountsReceivable';
import { AccountsPayable } from '../models/AccountsPayable';
import {
  FinancialMirrorDateMode,
  FinancialMirrorEntry,
  FinancialMirrorTotals
} from '../models/FinancialMirror';

@Injectable({ providedIn: 'root' })
export class FinancialMirrorService {
  private readonly pageSize = 500;

  constructor(
    private accountsReceivableService: AccountsReceivableService,
    private accountsPayableService: AccountsPayableService
  ) {}

  /**
   * Espelho do que entra (CR) e sai (CP) no período — sem lançamento manual.
   * due = por vencimento; settlement = por data de recebimento/pagamento (quando houver).
   */
  loadMirror(
    start: string,
    end: string,
    dateMode: FinancialMirrorDateMode = 'due',
    costCenterId?: number | null
  ): Observable<{ entries: FinancialMirrorEntry[]; totals: FinancialMirrorTotals }> {
    const arFilters =
      dateMode === 'settlement'
        ? { receiptDateStart: start, receiptDateEnd: end, page: 1, pageSize: this.pageSize }
        : { dueDateStart: start, dueDateEnd: end, page: 1, pageSize: this.pageSize };

    const apFilters =
      dateMode === 'settlement'
        ? { paymentDateStart: start, paymentDateEnd: end, page: 1, pageSize: this.pageSize }
        : { dueDateStart: start, dueDateEnd: end, page: 1, pageSize: this.pageSize };

    if (costCenterId != null) {
      (arFilters as { costCenterId?: number }).costCenterId = costCenterId;
      (apFilters as { costCenterId?: number }).costCenterId = costCenterId;
    }

    return forkJoin({
      receivables: this.accountsReceivableService.getAllAccountsReceivable(arFilters).pipe(
        map((r) => r.items || []),
        catchError(() => of([] as AccountsReceivable[]))
      ),
      payables: this.accountsPayableService.getAllAccountsPayable(apFilters).pipe(
        map((r) => r.items || []),
        catchError(() => of([] as AccountsPayable[]))
      )
    }).pipe(
      map(({ receivables, payables }) => {
        const entries: FinancialMirrorEntry[] = [
          ...receivables.map((ar) => this.mapReceivable(ar, dateMode)),
          ...payables.map((ap) => this.mapPayable(ap, dateMode))
        ];
        entries.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
        return { entries, totals: this.computeTotals(entries) };
      })
    );
  }

  private mapReceivable(ar: AccountsReceivable, dateMode: FinancialMirrorDateMode): FinancialMirrorEntry {
    const settled = this.isReceivableSettled(ar);
    const date =
      dateMode === 'settlement' && ar.receiptDate
        ? this.toDateKey(ar.receiptDate)
        : this.toDateKey(ar.dueDate);
    const amount =
      settled && ar.amountReceived != null
        ? Number(ar.amountReceived)
        : Number(ar.remainingAmount ?? ar.amountToReceive) || 0;

    return {
      id: `ar-${ar.id}`,
      source: 'accounts-receivable',
      sourceId: ar.id,
      type: 'Entrada',
      date,
      documentNumber: ar.documentNumber,
      description: ar.description || ar.documentNumber,
      status: ar.status,
      amount,
      costCenterId: ar.costCenterId,
      costCenterName: ar.costCenterName,
      counterpartyName: ar.customerName,
      contractOrOrderRef: ar.saleOrderNumber || ar.contractNumber,
      isSettled: settled
    };
  }

  private mapPayable(ap: AccountsPayable, dateMode: FinancialMirrorDateMode): FinancialMirrorEntry {
    const settled = this.isPayableSettled(ap);
    const date =
      dateMode === 'settlement' && ap.paymentDate
        ? this.toDateKey(ap.paymentDate)
        : this.toDateKey(ap.dueDate);
    const amount =
      settled && ap.amountPaid != null
        ? Number(ap.amountPaid)
        : Number(ap.remainingAmount ?? ap.amountToPay) || 0;

    return {
      id: `ap-${ap.id}`,
      source: 'accounts-payable',
      sourceId: ap.id,
      type: 'Saída',
      date,
      documentNumber: ap.documentNumber,
      description: ap.description || ap.documentNumber,
      status: ap.status,
      amount,
      costCenterId: ap.costCenterId,
      costCenterName: ap.costCenterName,
      counterpartyName: ap.supplierName,
      contractOrOrderRef: ap.purchaseOrderNumber,
      isSettled: settled
    };
  }

  private isReceivableSettled(ar: AccountsReceivable): boolean {
    const s = (ar.status || '').toLowerCase();
    return s === 'recebido' || (ar.amountReceived != null && Number(ar.amountReceived) > 0);
  }

  private isPayableSettled(ap: AccountsPayable): boolean {
    const s = (ap.status || '').toLowerCase();
    return s === 'pago' || (ap.amountPaid != null && Number(ap.amountPaid) > 0);
  }

  private toDateKey(value: Date | string | undefined): string {
    if (!value) return '';
    if (typeof value === 'string') return value.includes('T') ? value.split('T')[0] : value.substring(0, 10);
    return value.toISOString().split('T')[0];
  }

  computeTotals(entries: FinancialMirrorEntry[]): FinancialMirrorTotals {
    let totalIn = 0;
    let totalOut = 0;
    let countIn = 0;
    let countOut = 0;
    for (const e of entries) {
      if (e.type === 'Entrada') {
        totalIn += e.amount;
        countIn++;
      } else {
        totalOut += e.amount;
        countOut++;
      }
    }
    return {
      totalIn,
      totalOut,
      balance: totalIn - totalOut,
      countIn,
      countOut
    };
  }
}
